import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2, LogIn, LogOut, X, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { formatSessionDate, formatSessionTime, tzToday } from "@/utils/datetime";

type Kid = { person_id: string; name: string; arrival_at: string | null; departure_at: string | null; arrival_local: string | null; departure_local: string | null };
type GroupBlock = { group_id: string; group_name: string; children: Kid[] };

export default function KGDashboard() {
    const { t } = useTranslation();
    const { hasPerm, activeMembership } = useAuth();
    const tz = activeMembership?.tenant.timezone || "Asia/Baku";
    const [day, setDay] = useState(tzToday(tz));
    const [groups, setGroups] = useState<GroupBlock[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [pick, setPick] = useState<{ child: Kid; kind: "arrival" | "departure" } | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try { const r = await api.get(`/kg/dashboard?day=${day}`); setGroups(r.data.groups); }
        catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    }, [day]);
    useEffect(() => { load(); }, [load]);

    if (!hasPerm("kg.view")) return <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text)" }} data-testid="kg-perm-denied">{t("common.no_permission")}</div>;

    return (
        <div className="space-y-4" data-testid="kg-dashboard">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("nav.kindergarten")}</h1>
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>{t("kg.dashboard.subtitle")}</p>
                </div>
                <div className="flex gap-2 items-center">
                    <input data-testid="kg-day" type="date" value={day} onChange={e => setDay(e.target.value)} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                </div>
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {loading ? <div className="p-12 flex justify-center"><Loader2 className="animate-spin"/></div> : groups.length === 0 ? (
                <div className="surface-card p-6 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="kg-empty">{t("kg.no_kindergarten_group")}</div>
            ) : groups.map(g => (
                <div key={g.group_id} className="surface-card overflow-hidden" data-testid={`kg-group-${g.group_id}`}>
                    <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                        <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{g.group_name}</span>
                        <span className="text-xs" style={{ color: "var(--text-muted)" }}>{g.children.length} {t("kg.children")}</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}><th className="px-4 py-2">{t("common.name")}</th><th className="px-4 py-2">{t("kg.arrival")}</th><th className="px-4 py-2">{t("kg.departure")}</th><th className="px-4 py-2"></th></tr></thead>
                        <tbody>{g.children.map(k => (
                            <tr key={k.person_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`kg-child-${k.person_id}`}>
                                <td className="px-4 py-3"><Link to={`/kindergarten/child/${k.person_id}`} className="hover:underline" style={{ color: "var(--text)" }}>{k.name}</Link></td>
                                <td className="px-4 py-3 font-mono" style={{ color: k.arrival_local ? "var(--success-fg)" : "var(--text-muted)" }}>{k.arrival_local || "—"}</td>
                                <td className="px-4 py-3 font-mono" style={{ color: k.departure_local ? "var(--info-fg)" : "var(--text-muted)" }}>{k.departure_local || "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    {hasPerm("kg.mark_attendance") && (
                                        <div className="inline-flex gap-1">
                                            {!k.arrival_local && <button data-testid={`btn-arrival-${k.person_id}`} onClick={() => setPick({ child: k, kind: "arrival" })} className="rounded-md border px-2 py-1 text-xs inline-flex items-center gap-1" style={{ borderColor: "var(--border)", color: "var(--text)" }}><LogIn size={12}/>{t("kg.arrival")}</button>}
                                            {k.arrival_local && !k.departure_local && <button data-testid={`btn-departure-${k.person_id}`} onClick={() => setPick({ child: k, kind: "departure" })} className="rounded-md border px-2 py-1 text-xs inline-flex items-center gap-1" style={{ borderColor: "var(--border)", color: "var(--text)" }}><LogOut size={12}/>{t("kg.departure")}</button>}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            ))}
            {pick && <PickupDialog child={pick.child} kind={pick.kind} onClose={() => setPick(null)} onDone={() => { setPick(null); load(); }}/>}
        </div>
    );
}

function PickupDialog({ child, kind, onClose, onDone }: { child: Kid; kind: "arrival" | "departure"; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation();
    const [authorized, setAuthorized] = useState<any[]>([]);
    const [personId, setPersonId] = useState("");
    const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);

    useEffect(() => { (async () => {
        try { const r = await api.get(`/kg/authorized-people/${child.person_id}`); setAuthorized(r.data.items); }
        catch (e: any) { setErr(formatApiError(e)); }
    })(); }, [child.person_id]);

    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    const submit = async () => {
        if (!personId) { setErr(t("kg.pick_person_required") as string); return; }
        setBusy(true); setErr(null);
        try {
            const url = kind === "arrival" ? "/kg/attendance/arrival" : "/kg/attendance/departure";
            const key = kind === "arrival" ? "dropoff_person_id" : "pickup_person_id";
            await api.post(url, { child_person_id: child.person_id, [key]: personId });
            onDone();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="pickup-dialog">
            <div className="surface-card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t(`kg.${kind}`)} — {child.name}</h3>
                    <button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }}/></button>
                </div>
                <p className="text-xs mb-3 inline-flex items-center gap-1" style={{ color: "var(--warning-fg)" }}><ShieldAlert size={12}/> {t("kg.authorized_only_hint")}</p>
                <select data-testid="pickup-person" value={personId} onChange={e => setPersonId(e.target.value)} className="surface-input px-3 py-2 text-sm w-full mb-3" style={{ minHeight: 40 }}>
                    <option value="">{t("kg.pick_person")}</option>
                    {authorized.map(p => <option key={p.person_id} value={p.person_id}>{p.name} ({p.relation})</option>)}
                </select>
                {authorized.length === 0 && <div className="text-xs mb-3" style={{ color: "var(--error-fg)" }}>{t("kg.no_authorized")}</div>}
                {err && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="pickup-error">{err}</div>}
                <button data-testid="pickup-confirm" disabled={busy || !personId} onClick={submit} className="w-full rounded-md py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{busy ? <Loader2 className="animate-spin inline" size={14}/> : t("common.save")}</button>
            </div>
        </div>
    );
}
